import type { FilterCelice, Porocilo, VrsticaPorocila } from '@koda/shared';
import type { JSX } from 'react';

import { jeNegativen, odstotek, znesek } from '../lib/format';
import { useBesedila } from '../lib/jezik';
import type { Celica, DelCelice } from './PanelRazclenitve';

/** Razlika, ki ni izračunana, ker primerjava ni mogoča (5.1). */
const NI_IZRACUNANO = '—';

interface Lastnosti {
  /** Tri tabele Primerjave ali bloki Kazalnikov. */
  tabele: Porocilo[];
  /** Klik na številko odpre razčlenitev te celice. */
  naCelico: (celica: Celica) => void;
  /** Vrstica odprte razčlenitve; v tabeli je označena. */
  izbrana?: VrsticaPorocila | null;
  /** Naslov stolpca z nazivi vrstic; privzeto »Postavka«. */
  naslovVrstic?: string;
  /** Tabela v kartici (Kazalniki – Podrobno), naslov in pojasnilo v glavi kartice. */
  vKartici?: boolean;
  /** Skupina je vrstica nad svojimi postavkami, ne stolpec (Primerjava do meseca n). */
  skupineKotVrstice?: boolean;
  /** Stolpec »skupaj« je razlika: puščica, predznak in barva boljšega/slabšega izida. */
  puscice?: boolean;
  /** Brez naslova in pojasnila (tabela pod grafom v kartici, ki ju že ima). */
  brezNaslova?: boolean;
}

/**
 * Primerjava z letom prej: tri tabele ena pod drugo v isti postavitvi
 * (docs/ARHITEKTURA.md, 6.1).
 *
 * Stolpca skupina in postavka sta prilepljena ob vodoravnem drsenju, glava ob
 * navpičnem — pri dvanajstih mesecih je tabela široka. Vrstice so v vseh treh
 * tabelah enake in na istem mestu, zato jih je mogoče brati vzporedno.
 *
 * V tabelah let je celica brez knjižb prazna; v tabeli razlik je 0,00
 * podatek in razlike so rahlo obarvane: ker so odhodki zapisani negativno,
 * pozitivna razlika vedno pomeni boljši izid.
 *
 * Isto tabelo uporabljajo bloki Kazalnikov (docs/ARHITEKTURA.md, 6.3): tam
 * je številka vsota več delov (`deliStolpcev`), zato razčlenitev pokaže vsak
 * del posebej. Tabela brez skupin nima stolpca skupine; vrstica, ki ni vsota
 * knjižb (povprečje, razmerje, stanje denarja), ni klikljiva.
 */
export function TabelaPrimerjave({ tabele, naCelico, izbrana = null, naslovVrstic, vKartici = false, skupineKotVrstice = false, puscice = false, brezNaslova = false }: Lastnosti): JSX.Element {
  const b = useBesedila();

  /** Vrstica z istim ključem v drugi tabeli (za zneske delov razčlenitve). */
  const vrsticaV = (tabela: Porocilo | undefined, kljuc: string): VrsticaPorocila | undefined =>
    tabela === undefined
      ? undefined
      : kljuc === 'skupaj'
        ? (tabela.skupaj ?? undefined)
        : tabela.vrstice.find((v) => v.kljuc === kljuc);

  /** Deli razčlenitve stolpca razlik: knjižbe vsakega leta posebej. */
  const deli = (
    vrstica: VrsticaPorocila,
    filtri: FilterCelice[],
    znesekLeta: (v: VrsticaPorocila | undefined) => string,
  ): DelCelice[] =>
    filtri.map((f, j) => ({
      naslov: String(f.leto),
      filter: f,
      znesek: znesekLeta(vrsticaV(tabele[j], vrstica.kljuc)) || '0',
    }));

  const celica = (
    tabela: Porocilo,
    vrstica: VrsticaPorocila,
    i: number | 'skupaj',
  ): Celica => {
    const stolpec = i === 'skupaj' ? null : tabela.stolpci[i]!;
    const naslovObdobja =
      stolpec === null
        ? tabele[0]!.stolpci.length > 0
          ? `${tabele[0]!.stolpci[0]!.naziv} – ${tabele[0]!.stolpci[tabele[0]!.stolpci.length - 1]!.naziv}`
          : b.vsaObdobja
        : stolpec.naziv;
    // Kazalniki: celica ima svoje dele (sestavine) z napisi in zneski.
    const lastniDeli = i === 'skupaj' ? vrstica.deliSkupaj : vrstica.deliStolpcev?.[i];
    const filtri = i === 'skupaj' ? vrstica.filtriSkupaj : vrstica.filtriStolpcev?.[i];
    const osnova = {
      vrstica,
      obdobjeOd: stolpec === null ? vrstica.filter.obdobjeOd : stolpec.obdobje,
      obdobjeDo: stolpec === null ? vrstica.filter.obdobjeDo : stolpec.obdobje,
      naslovObdobja,
      znesek: i === 'skupaj' ? vrstica.skupaj : (vrstica.zneski[i] ?? ''),
    };
    if (lastniDeli !== undefined) {
      return {
        ...osnova,
        deli: lastniDeli.map((d) => ({ naslov: d.naziv, filter: d.filter, znesek: d.znesek })),
      };
    }
    if (filtri === undefined) return osnova;
    return {
      ...osnova,
      deli: deli(vrstica, filtri, (v) =>
        v === undefined ? '' : i === 'skupaj' ? v.skupaj : (v.zneski[i] ?? ''),
      ),
    };
  };

  const razredZneska = (tabela: Porocilo, z: string): string => {
    const razredi = ['znesek'];
    if (jeNegativen(z)) razredi.push('negativen');
    if (tabela.barvanjeRazlik === true && z !== '' && Number(z) !== 0) {
      razredi.push(Number(z) > 0 ? 'boljse' : 'slabse');
    }
    return razredi.join(' ');
  };

  const stevilka = (tabela: Porocilo, vrstica: VrsticaPorocila, i: number | 'skupaj'): JSX.Element => {
    const z = i === 'skupaj' ? vrstica.skupaj : (vrstica.zneski[i] ?? '');
    const kljuc = i === 'skupaj' ? 'skupaj' : String(i);
    const razred = i === 'skupaj' ? 'stevilka skupaj' : 'stevilka';
    // Prazna celica: v mesecu ni knjižb, zato tudi razčlenitve ni. V tabeli
    // razlik je razlika vedno število – prazna pomeni, da ni izračunana
    // (primerjalno leto nima podatkov za ta mesec), zato »—«.
    if (z === '') {
      return (
        <td key={kljuc} className={razred}>
          {tabela.barvanjeRazlik === true ? NI_IZRACUNANO : null}
        </td>
      );
    }
    const oblika = vrstica.enota === 'odstotek' ? odstotek(z) : znesek(z);
    // Razlika s puščico: pozitivna je vedno boljši izid (odhodki so negativni).
    const smer = puscice && i === 'skupaj' && Number(z) !== 0 ? (Number(z) > 0 ? 'boljse' : 'slabse') : null;
    const prikaz =
      smer === null ? (
        oblika
      ) : (
        <span className={`kz-smer ${smer}`}>
          <span aria-hidden="true">{smer === 'boljse' ? '▲' : '▼'}</span>
          {smer === 'boljse' ? `+${oblika}` : oblika}
        </span>
      );
    const deli = i === 'skupaj' ? vrstica.deliSkupaj : vrstica.deliStolpcev?.[i];
    // Razmerje, povprečje ali stanje ni vsota knjižb - ni česa razčleniti.
    if (!vrstica.klikljiva || (deli !== undefined && deli.length === 0)) {
      return (
        <td key={kljuc} className={razred}>
          <span className={razredZneska(tabela, z)}>{prikaz}</span>
        </td>
      );
    }
    return (
      <td key={kljuc} className={razred}>
        <button
          type="button"
          className={razredZneska(tabela, z)}
          onClick={() => {
            // Skupina je nadrejena vrstica; glava panela jo pokaže v poti.
            naCelico({ ...celica(tabela, vrstica, i), pot: vrstica.skupina ? [vrstica.skupina] : [] });
          }}
          title={i === 'skupaj' ? b.namigCelicaSkupaj : b.namigCelice}
        >
          {prikaz}
        </button>
      </td>
    );
  };

  /** Tabela brez skupin (bloki kazalnikov) nima stolpca skupine. */
  const imaSkupine = (tabela: Porocilo): boolean => !skupineKotVrstice && tabela.vrstice.some((v) => v.skupina !== undefined);
  const steviloStolpcev = (tabela: Porocilo): number =>
    (imaSkupine(tabela) ? 1 : 0) + 2 + tabela.stolpci.length + (tabela.prikaziOdstotekSkupaj === true ? 1 : 0);

  const vrsta = (tabela: Porocilo, vrstica: VrsticaPorocila): JSX.Element[] => [
    ...(skupineKotVrstice && vrstica.skupina !== undefined && vrstica.skupina !== ''
      ? [
          <tr key={`skupina-${vrstica.kljuc}`} className="skupina-vrstica">
            <th colSpan={steviloStolpcev(tabela)}>{vrstica.skupina}</th>
          </tr>,
        ]
      : []),
    <tr
      key={vrstica.kljuc}
      className={`${vrstica.vrstaVrstice ?? 'postavka'}${vrstica === izbrana ? ' izbrana' : ''}`}
    >
      {imaSkupine(tabela) && <th className="skupina">{vrstica.skupina ?? ''}</th>}
      <th className="postavka">{vrstica.naziv}</th>
      {vrstica.zneski.map((_, i) => stevilka(tabela, vrstica, i))}
      {stevilka(tabela, vrstica, 'skupaj')}
      {tabela.prikaziOdstotekSkupaj === true && (
        <td className="stevilka odstotek">
          {tabela.barvanjeRazlik === true && vrstica.skupaj === ''
            ? NI_IZRACUNANO
            : odstotek(vrstica.odstotekSkupaj ?? '')}
        </td>
      )}
    </tr>,
  ];

  return (
    <>
      {tabele.map((tabela) => (
        <section key={tabela.naslov} className={vKartici ? 'kz-karta kz-tabela' : brezNaslova ? 'kz-tabela-pod' : 'tabela-odsek'} aria-label={vKartici ? tabela.naslov : undefined}>
          {brezNaslova ? null : vKartici ? (
            <div className="kz-karta-naslov">
              <h2>{tabela.naslov}</h2>
              <div className="kz-karta-podnaslov">{tabela.pojasnilo}</div>
            </div>
          ) : (
            <>
              <h2>{tabela.naslov}</h2>
              <p className="podnaslov">{tabela.pojasnilo}</p>
            </>
          )}
          {tabela.opozorila.map((o) => (
            <p key={o} className="opozorilo">
              {o}
            </p>
          ))}
          <div className="okvir-primerjave">
            <table className={imaSkupine(tabela) ? 'porocilo primerjava' : 'porocilo primerjava brez-skupine'}>
              <thead>
                <tr>
                  {imaSkupine(tabela) && <th className="skupina">{b.skupina}</th>}
                  <th className="postavka">{naslovVrstic ?? b.postavka}</th>
                  {tabela.stolpci.map((s) => (
                    <th key={s.obdobje} className="stevilka">
                      {s.naziv}
                    </th>
                  ))}
                  <th className="stevilka skupaj">{tabela.nazivSkupaj ?? b.skupaj}</th>
                  {tabela.prikaziOdstotekSkupaj === true && <th className="stevilka odstotek">%</th>}
                </tr>
              </thead>
              <tbody>
                {tabela.vrstice.flatMap((v) => vrsta(tabela, v))}
                {tabela.skupaj !== null && vrsta(tabela, tabela.skupaj)}
              </tbody>
            </table>
          </div>
        </section>
      ))}
    </>
  );
}
